// ============================================================
//  src/components/Portfolio/Bharatanatyam.jsx
// ============================================================
import React, { useEffect, useRef, useState } from "react";
import "./Pages.css";
import portfolioVideo from "../../assets/Portfolio/bharatanatyam.mp4";
import { API_BASE, getImageUrl, getVideoUrl } from "../../config/api";

const BharatanatyamPhotography = () => {
  const videoRef = useRef(null);
  const galleryRef = useRef(null);
  const hasInteracted = useRef(false);

  const [isMuted, setIsMuted] = useState(true);
  const [galleryVisible, setGalleryVisible] = useState(true);
  const [portfolioItems, setPortfolioItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  /* FETCH DATA */
  useEffect(() => {
    setLoading(true);
    setError(null);
    fetch(`${API_BASE}?category=${encodeURIComponent('Bharatanatyam Photography')}`)
      .then((res) => {
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        return res.json();
      })
      .then((data) => {
        setPortfolioItems(Array.isArray(data) ? data : []);
        setLoading(false);
      })
      .catch((err) => {
        console.error(err);
        setError("Failed to load portfolio. Is XAMPP running?");
        setLoading(false);
      });
  }, []);

  /* VIDEO AUTOPLAY */
  useEffect(() => {
    const markInteracted = () => { hasInteracted.current = true; };
    window.addEventListener("click", markInteracted, { once: true });
    window.addEventListener("scroll", markInteracted, { once: true, passive: true });
    window.addEventListener("keydown", markInteracted, { once: true });

    const video = videoRef.current;
    if (!video) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          if (hasInteracted.current) {
            video.muted = false;
            video.play().then(() => setIsMuted(false)).catch(() => {
              video.muted = true;
              video.play();
              setIsMuted(true);
            });
          } else {
            video.muted = true;
            video.play().catch(() => {});
            setIsMuted(true);
          }
        } else {
          video.pause();
        }
      },
      { threshold: 0.5 }
    );
    observer.observe(video);

    return () => {
      window.removeEventListener("click", markInteracted);
      window.removeEventListener("scroll", markInteracted);
      window.removeEventListener("keydown", markInteracted);
      observer.disconnect();
    };
  }, []);

  /* GALLERY ANIMATION */
  useEffect(() => {
    const el = galleryRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) setGalleryVisible(true); },
      { threshold: 0.1 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  /* SOUND TOGGLE */
  const toggleMute = () => {
    if (!videoRef.current) return;
    const newMuted = !isMuted;
    videoRef.current.muted = newMuted;
    if (!newMuted) videoRef.current.play().catch(() => {});
    setIsMuted(newMuted);
  };

  return (
    <section className="portfolio-page">
      <div className="portfolio-hero">
        <video ref={videoRef} className="portfolio-video" autoPlay muted={isMuted} loop playsInline preload="auto">
          <source src={portfolioVideo} type="video/mp4" />
        </video>
        <div className="portfolio-overlay" />
        <button className="sound-btn" onClick={toggleMute} aria-label="Toggle sound">
          <span className="sound-icon">{isMuted ? <MuteIcon /> : <SoundIcon />}</span>
          <span className="sound-text">{isMuted ? "Sound Off" : "Sound On"}</span>
        </button>
        <div className="portfolio-content">
          <div className="portfolio-bottom">
            <h1 className="portfolio-title">Bharatanatyam Photography</h1>
          </div>
        </div>
      </div>

      <div className="weddings-section">
        <div className="weddings-header">
          <h2 className="weddings-title">BHARATANATYAM</h2>
        </div>

        {loading && <div className="gallery-loading"><p>Loading gallery...</p></div>}
        {error   && <div className="gallery-error"><p>{error}</p></div>}
        {!loading && !error && portfolioItems.length === 0 && (
          <div className="gallery-empty"><p>No photos uploaded yet. Add some from the admin panel.</p></div>
        )}

        {!loading && portfolioItems.length > 0 && (
          <div ref={galleryRef} className={`weddings-grid${galleryVisible ? " gallery-visible" : ""}`}>
            {portfolioItems.map((item, index) => (
              <div
                key={item.id}
                className={`grid-item ${index % 3 === 0 ? "item-tall" : index % 2 === 0 ? "item-hero" : "item-normal"}`}
                style={{ "--d": `${index * 0.1}s` }}
              >
                {item.type === "video" ? (
                  <video src={getVideoUrl(item.video_url)} autoPlay muted loop playsInline className="grid-video" />
                ) : (
                  <img src={getImageUrl(item.image)} alt={item.title} loading="lazy"
                    onError={(e) => { e.target.style.display = "none"; }} />
                )}
                {item.title && <div className="grid-item-caption">{item.title}</div>}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

const MuteIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <line x1="23" y1="9" x2="17" y2="15" /><line x1="17" y1="9" x2="23" y2="15" />
  </svg>
);
const SoundIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <polygon points="11 5 6 9 2 9 2 15 6 15 11 19 11 5" />
    <path d="M19.07 4.93a10 10 0 0 1 0 14.14" /><path d="M15.54 8.46a5 5 0 0 1 0 7.07" />
  </svg>
);

export default BharatanatyamPhotography;
